import { CityState, Building } from './types';

export class SafetyEngine {
  /**
   * Recalculates crime grids, fire risk grids, and manages active incidents/damages recovery.
   */
  public tickSafety(state: CityState) {
    state.crimeGrid = {};
    state.fireGrid = {};

    const policeStations: { x: number; y: number; range: number; power: number }[] = [];
    const fireStations: { x: number; y: number; range: number; power: number }[] = [];

    // 1. Locate all active/completed police and fire stations
    for (const key of Object.keys(state.buildings)) {
      const b = state.buildings[key];
      if (b.stage === 'completed' && b.zone === 'civic') {
        if (b.name.includes('Polizia')) {
          policeStations.push({ x: b.x, y: b.y, range: 6.5, power: 85 });
        } else if (b.name.includes('Pompieri')) {
          fireStations.push({ x: b.x, y: b.y, range: 6.5, power: 85 });
        }
      }
    }

    const width = state.width;
    const height = state.height;

    // 2. Compute safety grids cell by cell
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const key = `${x},${y}`;
        const b = state.buildings[key];

        if (!b) continue;

        // Base risk values depending on density and zone
        let baseCrime = 5;
        let baseFire = 5;

        if (b.stage === 'completed') {
          if (b.zone === 'residential') {
            baseCrime = b.density === 'low' ? 5 : (b.density === 'medium' ? 12 : 25);
            baseFire = b.density === 'low' ? 6 : (b.density === 'medium' ? 10 : 18);
          } else if (b.zone === 'commercial' || b.zone === 'office') {
            baseCrime = b.density === 'low' ? 8 : (b.density === 'medium' ? 15 : 30);
            baseFire = b.density === 'low' ? 8 : (b.density === 'medium' ? 12 : 22);
          } else if (b.zone === 'industrial') {
            baseCrime = b.density === 'low' ? 10 : (b.density === 'medium' ? 14 : 20);
            baseFire = b.density === 'low' ? 15 : (b.density === 'medium' ? 28 : 50); // Heavy industrial high fire risk
          }
        }

        // Apply proximity mitigations from Police (linear decay)
        let maxMitigationCrime = 0;
        for (const station of policeStations) {
          const dx = x - station.x;
          const dy = y - station.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < station.range) {
            const factor = 1.0 - dist / station.range;
            maxMitigationCrime = Math.max(maxMitigationCrime, station.power * factor);
          }
        }

        // Apply proximity mitigations from Fire caserme
        let maxMitigationFire = 0;
        for (const station of fireStations) {
          const dx = x - station.x;
          const dy = y - station.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < station.range) {
            const factor = 1.0 - dist / station.range;
            maxMitigationFire = Math.max(maxMitigationFire, station.power * factor);
          }
        }

        // Final calculated risk rates (clamped)
        const finalCrime = Math.max(5, Math.min(100, Math.round(baseCrime - maxMitigationCrime)));
        const finalFire = Math.max(5, Math.min(100, Math.round(baseFire - maxMitigationFire)));

        state.crimeGrid[key] = finalCrime;
        state.fireGrid[key] = finalFire;

        // Apply risk properties onto buildings
        b.crimeRisk = finalCrime;
        b.fireRisk = finalFire;

        // 3. Dynamic Emergency Incidents Spawner (Tornados are handled in Disasters, fires/crimes here)
        this.processIncidents(b, finalCrime, finalFire, state, key, fireStations, policeStations);
      }
    }
  }

  private processIncidents(
    b: Building,
    crimeRisk: number,
    fireRisk: number,
    state: CityState,
    _key: string,
    fireStations: any[],
    policeStations: any[]
  ) {
    if (b.stage === 'completed') {
      const pTick = 0.0006 * (state.simSpeed || 1); // small daily ticks probability

      // Crime outbreak (if crime risk is extremely high and random chance triggers)
      if (crimeRisk > 50 && Math.random() < pTick * (crimeRisk / 50)) {
        b.stage = 'damaged'; // Set to damaged/crime scene stage
        b.progress = 0;      // incident progress begins
        b.occupancy = 0;     // citizens leave immediately
        
        state.history.unshift({
          id: `crime_${Date.now()}`,
          date: `${state.date.day} / ${state.date.month} / ${state.date.year}`,
          description: `🚨 ATTENZIONE: Segnalata attività criminale a "${b.name}"! Dispiegate le volanti!`
        });
        
        // Subtract money penalty for incident response
        state.treasury = Math.max(0, state.treasury - 100);
      } 
      // Fire outbreak (if fire risk is extremely high and chance triggers)
      else if (fireRisk > 50 && Math.random() < pTick * (fireRisk / 50)) {
        b.stage = 'damaged'; // Set to damaged / burning stage
        b.progress = 0;
        b.occupancy = 0;

        state.history.unshift({
          id: `fire_${Date.now()}`,
          date: `${state.date.day} / ${state.date.month} / ${state.date.year}`,
          description: `🔥 EMERGENZA: Scoppiato un incendio a "${b.name}"! Dispiegati i Vigili del Fuoco!`
        });

        state.treasury = Math.max(0, state.treasury - 150);
      }
    } 
    else if (b.stage === 'damaged') {
      // Incident recovery: building progress acts as "emergency response arrival & repair progress"
      // Recovery is fast if covered by emergency stations, extremely slow or stalled if completely out of range!
      const isFireProtected = fireStations.length > 0 && b.fireRisk! < 50;
      const isPoliceProtected = policeStations.length > 0 && b.crimeRisk! < 50;
      
      const recoveryRate = (isFireProtected || isPoliceProtected) ? 14 : 3; // 14% daily recovery vs 3% slums recovery
      b.progress += Math.floor(Math.random() * 6) + recoveryRate;

      if (b.progress >= 100) {
        b.stage = 'completed';
        b.progress = 100;
        b.occupancy = b.capacity; // citizens return!

        state.history.unshift({
          id: `solved_${Date.now()}`,
          date: `${state.date.day} / ${state.date.month} / ${state.date.year}`,
          description: `✅ RISOLTO: Ripristinata la sicurezza ed estinte le fiamme a "${b.name}"!`
        });
      }
    }
  }
}
